import React, { useState, useEffect } from 'react';
import Footer from '../../layout/Footer';
import ViewTax from './ViewTax';
import { Container, Row, Col, Card, Form, Nav, Button } from 'react-bootstrap';
import SimpleBarReact from "simplebar-react";
import { getFromAPI, postToAPI, putToAPI, formatISODateWithTimezone, handleDateFormat } from '../../../Utils/utils';
import GSTConfigurationData from "./gst-configuration-data.json";
import { toast } from "react-toastify";
import { GSTProvider, useGST } from './getGST';
import { gstContext1 } from './gstContext';
import Icon from '@mdi/react';
import { mdiFormatListBulletedSquare, mdiPlus, mdiMagnify } from '@mdi/js';
import { fetchTaxData } from '../../../Utils/function_list';
import FormInputs from '../../../Utils/FormInputs';
import messages from '../../../Utils/lang';

function GSTConfiguration() {
  // Initial GST input data structure
  const initialGstInputData = {
    name: '',
    taxrate: '',
    country_id: 0,
    applicable_date: formatISODateWithTimezone(new Date()),
    iscombined: 1, // Default to 1 (Simple Tax)
    child_ids: 'NA',
    isdefault: 0,
    type: 1,
    description: 'NA',
    created_branch_id: 0,
    isdeleted: 0,
    created_user_id: 0,
    last_modified_user_id: 0,
    last_modified_date_time: formatISODateWithTimezone(new Date()),
    created_date_time: formatISODateWithTimezone(new Date()),
    reserve_varchar1: 'NA',
    reservs_varchar2: 'NA',
    reserve_float1: 0.0,
    reserve_float2: 0.0,
    reserve_int1: 0,
    reserve_int2: 0,
    reserve_double1: 0.0,
    reserve_date1: '2024-09-18',
  };

  // State declarations
  const [gstInputData, setGstInputData] = useState(initialGstInputData);
  const [countryData, setCountryData] = useState();
  const [searchTerm, setSearchTerm] = useState('');
  const [getGST, setGST] = useState([]);
  const [enterTaxRate1, setEnterTaxRate1] = useState('');
  const [enterTaxRate2, setEnterTaxRate2] = useState('');
  const [selectedGSTId, setSelectedGSTId] = useState('');
  const { gstData, fetchGSTData } = useGST();
  const [taxType, setTaxType] = useState('simple');
  const [isTaxRateDisabled, setTaxRateDisabled] = useState(false);
  const [Tax, setTax] = useState([]);

  // Reset form to initial state
  const handleCancel = () => {
    setGstInputData(initialGstInputData);
    setTaxType('simple');
    setEnterTaxRate1('');
    setEnterTaxRate2('');
  };

  // Fetch GST data when selectedGSTId changes
  useEffect(() => {
    fetchGSTData(setGST);
  }, [selectedGSTId]);

  // Consolidated handler for all form changes
const handleGstConChange = (arg1, arg2, arg3) => {
    // Check if arg1 is an event object (DOM event) or a custom argument (from FormInputs)
    if (arg1 && arg1.target) {
      // Handle DOM event (e.g., direct input change)
      const { name, value, type, checked } = arg1.target;
  
      if (type === 'checkbox') {
        setGstInputData(prev => ({
          ...prev,
          [name]: checked ? 1 : 0,
        }));
        return;
      }
  
      if (name === 'taxType') {
        setTaxType(value);
        if (value === 'simple') {
          setGstInputData(prev => ({
            ...prev,
            iscombined: 1,
            child_ids: '',
            taxrate: prev.taxrate || '',
          }));
        } else if (value === 'combine') {
          setGstInputData(prev => ({
            ...prev,
            iscombined: 2,
            taxrate: '',
            child_ids: [enterTaxRate1, enterTaxRate2].filter(Boolean).join(','),
          }));
        }
        return;
      }
  
      if (name === 'enterTaxRate1') {
        setEnterTaxRate1(value);
        if (taxType === 'combine') {
          setGstInputData(prev => ({
            ...prev,
            child_ids: [value, enterTaxRate2].filter(Boolean).join(','),
          }));
        }
        return;
      }
  
      if (name === 'enterTaxRate2') {
        setEnterTaxRate2(value);
        if (taxType === 'combine') {
          setGstInputData(prev => ({
            ...prev,
            child_ids: [enterTaxRate1, value].filter(Boolean).join(','),
          }));
        }
        return;
      }
  
      setGstInputData(prev => ({
        ...prev,
        [name]: name === 'type' ? parseInt(value) : value,
      }));
    } else {
      // Handle custom arguments from FormInputs (index, field, value)
      const field = arg2; // field name
      const value = arg3; // field value
  
      if (field === 'taxType') {
        setTaxType(value);
        if (value === 'simple') {
          setGstInputData(prev => ({
            ...prev,
            iscombined: 1,
            child_ids: '',
            taxrate: prev.taxrate || '',
          }));
        } else if (value === 'combine') {
          setGstInputData(prev => ({
            ...prev,
            iscombined: 2,
            taxrate: '',
            child_ids: [enterTaxRate1, enterTaxRate2].filter(Boolean).join(','),
          }));
        }
        return;
      }
  
      if (field === 'enterTaxRate1') {
        setEnterTaxRate1(value);
        if (taxType === 'combine') {
          setGstInputData(prev => ({
            ...prev,
            child_ids: [value, enterTaxRate2].filter(Boolean).join(','),
          }));
        }
        return;
      }
  
      if (field === 'enterTaxRate2') {
        setEnterTaxRate2(value);
        if (taxType === 'combine') {
          setGstInputData(prev => ({
            ...prev,
            child_ids: [enterTaxRate1, value].filter(Boolean).join(','),
          }));
        }
        return;
      }
  
      if (field === 'isdefault') {
        setGstInputData(prev => ({
          ...prev,
          [field]: value === '1' ? 1 : 0,
        }));
        return;
      }
  
      setGstInputData(prev => ({
        ...prev,
        [field]: field === 'type' ? parseInt(value) : value,
      }));
    }
  };

  // Add new GST entry
  const handleAddGst = async () => {
    if (!gstInputData.name.trim()) {
      toast.error(messages.gst.required_name, 'error');
      return;
    }
    if (!gstInputData.taxrate.trim()) {
      toast.error(messages.gst.required_tax_rate, 'error');
      return;
    }
    if (gstData.some(item => item.name.toLowerCase() === gstInputData.name.toLowerCase())) {
      toast.error(messages.gst.already_exist);
      return;
    }

    const combinedChildIds = `${enterTaxRate1},${enterTaxRate2}`;
    const names = combinedChildIds.split(',').map(id => {
      const taxRate = Tax.find(rate => rate.id === parseInt(id.trim()));
      return taxRate ? taxRate.name : 'NA';
    });
    const childDescriptionString = names.join(' + ');

    const updatedGstInputData = {
      ...gstInputData,
      description: childDescriptionString,
      child_ids: combinedChildIds,
    };

    try {
      const response = await postToAPI("FtsTaxes/", updatedGstInputData);
      if (response.status) {
        toast.success('GST Data Added Successfully', 'success');
        const newUnitId = response.data.id;
        setSelectedGSTId(newUnitId);
        document.getElementById("pills-view-tax-tab")?.click();
        setTimeout(() => setFocusOnSidebarItem(newUnitId), 100);
        setGstInputData(initialGstInputData);
        setEnterTaxRate1('');
        setEnterTaxRate2('');
        fetchGSTData();
        
      }
    } catch (error) {
      toast.error('An unexpected error occurred', 'error');
    }
  };

  // Filter GST data based on search term
  const filteredGstData = gstData.filter(admin =>
    admin.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  // Sidebar item click handler
  useEffect(() => {
    const sidebarItems = document.querySelectorAll(".left-link-item");
    sidebarItems.forEach(item => {
      item.addEventListener('click', function () {
        document.querySelector(".left-link-item a.active")?.classList.remove("active");
        const link = this.querySelector('a');
        link?.classList.add('active');
        const selectedGSTId = this.getAttribute('data-id');
        setSelectedGSTId(selectedGSTId);
        document.getElementById("pills-view-tax-tab")?.click();
      });
    });
    return () => sidebarItems.forEach(item => item.removeEventListener('click', () => { }));
  }, [filteredGstData]);

  // Handle initial selection and updates
  useEffect(() => {
    if (gstData.length > 0 && (!selectedGSTId || !gstData.find(gst => gst.id === selectedGSTId))) {
      setSelectedGSTId(gstData[0].id);
      setFocusOnSidebarItem(gstData[0].id);
    }
  }, [gstData, selectedGSTId]);

  // Set focus on sidebar item
  const setFocusOnSidebarItem = (unitId) => {
    document.querySelectorAll(".left-link-item a").forEach(link => link.classList.remove("active"));
    const newItem = document.querySelector(`.left-link-item[data-id="${unitId}"] a`);
    if (newItem) {
      newItem.classList.add("active");
      newItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Fetch tax data
  useEffect(() => {
    fetchTaxData(setTax);
  }, []);

  // Form configuration for FormInputs component
  const gstConRows = [
    {
      columns: [
        { size: { lg: 8 }, label: 'Tax Name', type: 'text', name: 'name', id: 'name', value: gstInputData.name, placeholder: 'Enter tax name...', errormsg: '' },
        {
          size: { xl: 4 }, type: 'radios', class: 'text-xl-center mt-xl-4', value: taxType, name: 'taxType',
          options: [
            { value: 'simple', label: 'Simple Tax', id: 'simple-tax-check', checked: taxType === 'simple' },
            { value: 'combine', label: 'Combine Tax', id: 'combine-tax-check', checked: taxType === 'combine' },
          ]
        },
        { size: { xl: 4 }, label: 'Tax Rate', type: 'text', name: 'taxrate', id: 'taxrate', value: gstInputData.taxrate, placeholder: 'Enter tax rate...', errormsg: '' },
        {
          size: { md: 6, xl: 4 }, label: 'Tax Rate 1', type: 'select', name: 'enterTaxRate1', id: 'enterTaxRate1', value: enterTaxRate1, disabled: gstInputData.iscombined === 1, errormsg: '',
          options: [{ value: '', label: 'Select Tax Rate 1' }, ...Tax.map(item => ({ value: item.id, label: item.name }))]
        },
        {
          size: { md: 6, xl: 4 }, label: 'Tax Rate 2', type: 'select', name: 'enterTaxRate2', id: 'enterTaxRate2', value: enterTaxRate2, disabled: gstInputData.iscombined === 1, errormsg: '',
          options: [{ value: '', label: 'Select Tax Rate 2' }, ...Tax.map(item => ({ value: item.id, label: item.name }))]
        },
        {
          size: { xl: 4 }, type: 'radios', value: parseInt(gstInputData.type), name: 'type',
          options: [
            { value: 1, label: 'Inclusive', id: 'inclusive-check', checked: parseInt(gstInputData.type) === 1 },
            { value: 2, label: 'Exclusive', id: 'exclusive-check', checked: parseInt(gstInputData.type) === 2 },
          ]
        },
        {
          size: { sm: 12 }, type: 'checkboxes', value: gstInputData.isdefault, name: 'isdefault',
          checkboxes: [
            { value: '1', id: 'isdefault', checked: gstInputData.isdefault === 1, label: 'Set this tax as default tax. It will be used by default for business.' },
          ]
        },
      ]
    },
  ];

  const isFormValid = () => gstInputData.name && gstInputData.taxrate;

  const gstConButtons = [
    { variant: "primary", text: "Add", onClick: handleAddGst, disabled: !isFormValid() },
    { variant: "secondary", text: "Cancel", onClick: handleCancel },
  ];

  return (
    <gstContext1.Provider value={selectedGSTId}>
      <div className="page-wrapper">
        <div className='page-content'>
          <Container>
            <Row>
              <Col>
                <Card>
                  <Card.Body>
                    <div className='d-md-flex justify-content-between align-items-center'>
                      <Form className='custom-form-width'>
                        <div className='custom-search-bar'>
                          <Icon path={mdiMagnify} size={1} className="custom-search-input-icon" />
                          <Form.Control type="text" placeholder={`Search gst name from ${filteredGstData.length} gst`} name='gstSearch' id='gstSearch' onChange={handleSearchChange} value={searchTerm} />
                        </div>
                      </Form>
                      <div className="custom-top-nav-tab mt-3 mt-md-0">
                        <ul className="nav nav-pills btn-list" id="pills-tab" role="tablist">
                          <li className="nav-item" role="presentation">
                            <button className="nav-link active" id="pills-view-tax-tab" data-bs-toggle="pill" data-bs-target="#pills-view-tax" type="button" role="tab" aria-controls="pills-view-tax" aria-selected="true">
                              <Icon path={mdiFormatListBulletedSquare} className="btn-icon me-1" />View Tax
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button className="nav-link" id="pills-new-tax-tab" data-bs-toggle="pill" data-bs-target="#pills-new-tax" type="button" role="tab" aria-controls="pills-new-tax" aria-selected="false">
                              <Icon path={mdiPlus} className="btn-icon me-1" />New Tax
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col lg={4} xxl={3}>
                <Card className='procust-left-list leftbar-list'>
                  <SimpleBarReact>
                    <Card.Body>
                      <div className='left-list-bar'>
                        {filteredGstData.map((data, i) => (
                          <Nav.Item className='left-link-item' key={i} data-id={data.id} onClick={() => { setSelectedGSTId(data.id); setFocusOnSidebarItem(data.id); }}>
                            <Nav.Link className={data.id === selectedGSTId ? "active" : ""}>
                              <h5 className='product-tab-title'>{data.name}</h5>
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </div>
                    </Card.Body>
                  </SimpleBarReact>
                </Card>
              </Col>
              <Col lg={8} xxl={9}>
                <div className="tab-content custom-top-nav-tab" id="pills-tabContent">
                  <div className="tab-pane fade show active" id="pills-view-tax" role="tabpanel" aria-labelledby="pills-view-tax-tab" tabIndex="0">
                    <ViewTax setSelectedGSTId={setSelectedGSTId} />
                  </div>
                  <div className="tab-pane fade" id="pills-new-tax" role="tabpanel" aria-labelledby="pills-new-tax-tab" tabIndex="0">
                    <Card className='right-content-details'>
                      <Card.Body>
                        <Card.Title>New Tax</Card.Title>
                        <FormInputs Buttons={gstConButtons} rows={gstConRows} handleInputChange={handleGstConChange} handleSubmit={handleAddGst} />
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <Footer />
      </div>
    </gstContext1.Provider>
  );
}

export default function CustomersWithProvider() {
  return (
    <GSTProvider>
      <GSTConfiguration />
    </GSTProvider>
  );
}